// ==========================================
// INDUSTRIES SECTION 2 — ALL STATIC DATA HERE
// ==========================================

// DEFAULT HEADING
export const defaultHeading = {
  title: "PROVEN CLIENT IMPACT",
  subtitle: "Turning financial complexity into clear, actionable results.",
};

// FINANCIAL SERVICES HEADING
export const financialServicesHeading = {
  title: "OUR WORK IN ACTION",
  subtitle:
    "Focused on outcomes that improve value, resilience, and investor trust.",
};

// HEALTHCARE HEADING
export const healthcareHeading = {
  title: "OUTCOMES THAT IMPROVE CARE",
  subtitle:
    "Helping healthcare organizations operate smarter, safer, and more sustainably.",
};

// ==========================================
// DEFAULT STATS + BLUE CONTENT
// ==========================================
export const defaultStats = [
  { number: "12.8%", label: "Average Annual Return" },
  { number: "98%", label: "Client Retention Rate" },
  { number: "500+", label: "Institutional Clients" },
  { number: "15", label: "Global Markets" },
];

export const defaultBlueContent = {
  title: "Navigating Financial Complexity",
  paragraphs: [
    "In today's rapidly evolving financial landscape, institutions face unprecedented challenges in market volatility, regulatory compliance, and digital transformation.",
    "We combine deep industry knowledge with cutting-edge analytics to deliver insights that drive sustainable growth and optimize risk management.",
  ],
  image: "/assets/images/finance-team.png",
};

// ==========================================
// HEALTHCARE
// ==========================================
export const healthcareStats = [
  { number: "25%", label: "Healthcare Spend Wasted" },
  { number: "30–40%", label: "Administrative Load Reduced by Digitization" },
  { number: "15–20%", label: "Avoidable Readmission Reduction" },
  { number: "N/A", label: "Transformation Initiatives" },
];

export const healthcareBlueContent = {
  title: "UNDERSTANDING HEALTHCARE BETTER",
  paragraphs: [
    "Healthcare is shifting faster than most industries, and leaders are under pressure to deliver better outcomes with tighter budgets and rising patient expectations. Pestel & Partners deliver this flawlessly!",
    "We help healthcare organizations with grounded financial insight, smarter workflow strategies, and transformational plans.",
  ],
  image: "/assets/images/finance-team.png",

  // ⭐ HIGHLIGHTS ADDED
  highlights: [
    {
      icon: "/assets/icons/path-to-icon1.png",
      text: "Healthcare organizations waste up to 25% of expenditure.",
    },
    {
      icon: "/assets/icons/path-to-icon2.png",
      text: "Digital adoption can reduce administrative load by 30–40%.",
    },
    {
      icon: "/assets/icons/path-to-icon3.png",
      text: "Predictive analysis can lower avoidable readmissions by 15–20%.",
    },
  ],
};

// ==========================================
// ASSET MANAGEMENT
// ==========================================
export const assetManagementStats = [
  { number: "18%", label: "Operational Efficiency Gains" },
  { number: "35%", label: "Risk Exposure Reduction" },
  { number: "50+", label: "Asset Manager Engagements" },
  { number: "20 Years", label: "Industry Experience" },
];

export const assetManagementBlueContent = {
  title: "NAVIGATING ASSET COMPLEXITY",
  paragraphs: [
    "Every asset manager faces a moment when spreadsheets, market movements, and client demands no longer tell a simple story, and navigating that moment defines performance. With markets evolving faster than traditional models can keep up, teams need more than data; they need clarity, direction, and confidence.",
    "Here comes Pestel & Partners! We support this shift with transparent financial frameworks and practical decision pathways that make uncertainty manageable and long-term value more achievable.",
  ],
  image: "/assets/images/finance-team.png",
  highlights: [],
};

// ==========================================
// BANKING
// ==========================================
export const bankingStats = [
  { number: "27%", label: "Compliance Cost Reduction" },
  { number: "40%", label: "Process Automation Boost" },
  { number: "3x", label: "Faster Customer Onboarding" },
  { number: "75%", label: "Customer Experience Improvement" },
];

export const bankingBlueContent = {
  title: "NAVIGATING BANKING’S NEW COMPLEXITY",
  paragraphs: [
    " Banking complexity doesn’t show up all at once; it builds quietly through regulation updates, shifting customer needs, and the pressure to modernize faster than competitors. Our role is to help banks cut through that noise and focus on what truly drives long-term strength.  .",
    "What we bring? We present clarity to financial decisions, upgrade slow processes, and support teams in building secure, customer-first operations that can navigate uncertainty without losing momentum. With Pestel & Partners, banks gain a partner that simplifies complexity and strengthens confidence at every step. ",
  ],
  image: "/assets/images/finance-team.png",
  highlights: [],
};

// ==========================================
// FINANCIAL ADVISORY
// ==========================================
export const financialAdvisoryStats = [
  { number: "30%", label: "Decision Speed Improvement" },
  { number: "45%", label: "Better Strategic Allocation" },
  { number: "60%", label: "Risk Reduction Impact" },
  { number: "80+", label: "Advisory Projects Delivered" },
];

export const financialAdvisoryBlueContent = {
  title: "FINANCIAL COMPLEXITY, SIMPLIFIED",
  paragraphs: [
    "Financial complexity isn’t the challenge; knowing how to navigate is. Leaders today face shifting markets, rising compliance expectations, and decisions that demand speed with precision.",
    "Here we are! Pestel & Partners brings clarity to this landscape by translating numbers into insight, insight into action, and action into long-term value. Our advisory experts partner closely with clients to simplify uncertainty, strengthen decision frameworks for sustainable financial health.",
  ],
  image: "/assets/images/finance-team.png",
  highlights: [],
};

// ==========================================
// FINANCIAL SERVICE TAX
// ==========================================
export const financialServiceTaxStats = [
  { number: "22%", label: "Tax Efficiency Boost" },
  { number: "35%", label: "Compliance Risk Reduction" },
  { number: "50+", label: "Tax Strategy Engagements" },
  { number: "99%", label: "Regulatory Accuracy" },
];

export const financialServiceTaxBlueContent = {
  title: "NAVIGATING TODAY’S TAX REALITY",
  paragraphs: [
    "Tax functions today sit at the center of financial clarity, business risk, and regulatory accountability; yet most teams are expected to deliver more with limited time, rising scrutiny, and constantly changing rules. What used to be a compliance exercise has become a strategic finance responsibility, influencing investment choices, cross-border operations, cash flow planning, and long-term value creation.",
    "Pestel & Partners can be your helping hand! We support organizations in navigating this shift with practical, transparent, and future-ready tax strategies.",
  ],
  image: "/assets/images/finance-team.png",
  highlights: [],
};

// ==========================================
// FINTECH
// ==========================================
export const fintechStats = [
  { number: "50%", label: "Faster Digital Scaling" },
  { number: "3x", label: "Risk Framework Strengthening" },
  { number: "70%", label: "Operational Transparency Boost" },
  { number: "120+", label: "FinTech Projects Delivered" },
];

export const fintechBlueContent = {
  title: "NAVIGATING FINTECH COMPLEXITY",
  paragraphs: [
    "FinTech evolves faster than most businesses can react, and every new idea comes with new financial, regulatory, and operational consequences. That is why the real challenge isn’t launching digital products; it’s scaling them responsibly.",
    "At Pestel & Partners, we help FinTech leaders move with confidence by grounding innovation in financial discipline, strengthening risk frameworks, and building transparent systems that regulators and customers can trust.",
  ],
  image: "/assets/images/finance-team.png",
  highlights: [],
};

// ==========================================
// INSURANCE
// ==========================================
export const insuranceStats = [
  { number: "25%", label: "Operational Cost Optimization" },
  { number: "40%", label: "Claims Efficiency Improvement" },
  { number: "3x", label: "Customer Confidence Boost" },
  { number: "80+", label: "Insurance Engagements" },
];

export const insuranceBlueContent = {
  title: "GUIDING INSURERS THROUGH MODERN UNCERTAINTY",
  paragraphs: [
    "Customers want insurance that feels reliable, easy to understand, and responsive when life changes unexpectedly. But behind the scenes, insurers deal with rising claims costs, tighter rules, and pressure to innovate quickly. But this is not the end!",
    "Count on Pestel &Partners! We work closely with insurance leaders to strengthen financial controls, streamline processes, and design strategies that improve customer confidence while keeping operations resilient.",
  ],
  image: "/assets/images/finance-team.png",
  highlights: [],
};

// ==========================================
// OTHER SECTORS
// ==========================================
export const oilAndGasStats = [
  { number: "22%", label: "Cost Reduction Achieved" },
  { number: "40%", label: "Faster Reporting Cycles" },
  { number: "5000+", label: "Assets Digitally Monitored" },
  { number: "12", label: "Operational Efficiency Programs" },
];

export const oilAndGasBlueContent = {
  title: "RESHAPING WHAT’S POSSIBLE IN OIL & GAS",
  paragraphs: [
    "Upstream spending exceeds $1.4 trillion annually, and companies need smarter financial strategies.",
  ],
  image: "/assets/images/finance-team.png",
  highlights: [
    {
      icon: "/assets/icons/path-to-icon1.png",
      text: "Digital tools cut operating costs by up to 15%.",
    },
    {
      icon: "/assets/icons/path-to-icon2.png",
      text: "Predictive systems reduce downtime by 30%.",
    },
    {
      icon: "/assets/icons/path-to-icon3.png",
      text: "ESG adoption boosts investor interest by 25%.",
    },
  ],
};

export const powerAndUtilitiesStats = [
  { number: "15–20%", label: "Grid Reliability Improvement" },
  { number: "25%", label: "Peak Load Disruption Reduction" },
  { number: "20–30%", label: "Outage Duration Reduction" },
  { number: "10–12%", label: "System Efficiency Boost" },
];

export const powerAndUtilitiesBlueContent = {
  title: "TRANSFORMING UTILITIES FOR AN ENERGY FUTURE",
  paragraphs: [
    "The utilities sector shifts toward electrification, smarter grids, and renewables.",
  ],
  image: "/assets/images/finance-team.png",
  highlights: [
    {
      icon: "/assets/icons/path-to-icon1.png",
      text: "Smart grids reduce outage duration by 20–30%.",
    },
    {
      icon: "/assets/icons/path-to-icon2.png",
      text: "Digital metering cuts operational losses by up to 18%.",
    },
    {
      icon: "/assets/icons/path-to-icon3.png",
      text: "Renewable integration boosts system efficiency by 10–12%.",
    },
  ],
};

export const wasteManagementStats = [
  { number: "10%", label: "Processing Cost Reduction" },
  { number: "15–25%", label: "Efficiency Improvement" },
  { number: "30%", label: "Landfill Dependency Reduction" },
  { number: "3.4B", label: "Tonnes of Global Waste by 2050" },
];

export const wasteManagementBlueContent = {
  title: "ENGINEERING THE FUTURE OF RESPONSIBLE WASTE",
  paragraphs: [
    "Organizations must modernize quickly to meet regulatory and environmental expectations.",
  ],
  image: "/assets/images/finance-team.png",
  highlights: [
    {
      icon: "/assets/icons/path-to-icon1.png",
      text: "Reduce processing costs by up to 20%.",
    },
    {
      icon: "/assets/icons/path-to-icon2.png",
      text: "Cut waste-collection inefficiencies by 15–25%.",
    },
    {
      icon: "/assets/icons/path-to-icon3.png",
      text: "Recycling models lower landfill dependency by 30%.",
    },
  ],
};

export const miningAndMineralsStats = [
  { number: "4x", label: "Mineral Demand by 2040" },
  { number: "30%", label: "Downtime Reduction" },
  { number: "20%", label: "Lower Operating Costs" },
  { number: "N/A", label: "Transformation Programs" },
];

export const miningAndMineralsBlueContent = {
  title: "SHAPING THE FUTURE OF MINERAL EXTRACTION",
  paragraphs: [
    "Digital tools, automated workflows, and transparent reporting are no longer “future plans”—they are the backbone of stable production and long-term value creation. Pestel & Partners helps mining teams make this transition practical and achievable, building transformation strategies that strengthen margins, reduce risks, and prepare operations for the next decade of mineral demand.",
  ],
  image: "/assets/images/finance-team.png",
  highlights: [
    {
      icon: "/assets/icons/path-to-icon1.png",
      text: "Global mineral demand projected to rise 4x by 2040.",
    },
    {
      icon: "/assets/icons/path-to-icon2.png",
      text: "Predictive maintenance reduces equipment downtime by up to 30%.",
    },
    {
      icon: "/assets/icons/path-to-icon3.png",
      text: "Autonomous haulage systems deliver 15–20% lower operating costs.",
    },
  ],
};
// =========================== LIFE SCIENCE DATA

export const lifeScienceHeading = {
  title: "OUTCOME YOU CAN TRUST",
  subtitle:
    "Delivering clarity, control, and confidence across the full life-sciences value chain.",
};
export const lifeScienceStats = [
  { number: "35%", label: "R&D Cost Increase Over Last Decade" },
  { number: "6–8%", label: "Expected Annual Global R&D Growth" },
  { number: "40%", label: "Early-stage Development Time Reduced via AI" },
  { number: "25–30%", label: "Compliance Errors Reduced via Digital Systems" },
];

export const lifeScienceBlueContent = {
  title: "NAVIGATING SCIENTIFIC COMPLEXITY ",
  paragraphs: [
    "With R&D costs rising by over 35% in the last decade, organizations need sharper decision-making and stronger operational discipline. Pestel & Partners supports life science companies with market-ready financial strategies for a sustainable future. ",
  ],
  image: "/assets/images/finance-team.png", // You can change image if needed

  highlights: [
    {
      icon: "/assets/icons/path-to-icon1.png",
      text: "Global R&D spending in life sciences is expected to grow 6–8% annually.",
    },
    {
      icon: "/assets/icons/path-to-icon2.png",
      text: "AI drug discovery can cut early-stage development time by up to 40%.",
    },
    {
      icon: "/assets/icons/path-to-icon3.png",
      text: "Digital quality systems reduce compliance errors by 25–30%.",
    },
  ],
};

export const privateEquityHeading = {
  title: "VALUE YOU CAN MEASURE ",
  subtitle:
    "Driving measurable financial outcomes across deals, portfolios, and exits.",
};
export const privateEquityStats = [
  { number: "35%", label: "R&D Cost Increase Over Last Decade" },
  { number: "6–8%", label: "Expected Annual Global R&D Growth" },
  { number: "40%", label: "Early-stage Development Time Reduced via AI" },
  { number: "25–30%", label: "Compliance Errors Reduced via Digital Systems" },
];
export const privateEquityBlueContent = {
  title: "NAVIGATING PRIVATE EQUITY COMPLEXITY",
  paragraphs: [
    "Private equity today operates inside one of the most complex environments the industry has ever seen. Deal competition has surged 40% in the last five years; dry powder sits at over $2.6 trillion globally, and LPs are demanding faster, more transparent value creation.",
  ],
  image: "/assets/images/finance-team.png",
  highlights: [
    {
      icon: "/assets/icons/path-to-icon1.png",
      text: "Deal cycles are 35-50% shorter than they were a decade ago. ",
    },
    {
      icon: "/assets/icons/path-to-icon2.png",
      text: "73% of PE leaders report rising operational complexity. ",
    },
    {
      icon: "/assets/icons/path-to-icon3.png",
      text: "Portfolio value creation now contributes over 50% of total returns.",
    },
  ],
};
export const professionalServiceHeading = {
  title: "RESULTS YOU CAN TRUST",
  subtitle:
    "Empowering professional service firms for higher delivery performance.",
};
export const professionalServiceStats = [
  { number: "35%", label: "R&D Cost Increase Over Last Decade" },
  { number: "6–8%", label: "Expected Annual Global R&D Growth" },
  { number: "40%", label: "Early-stage Development Time Reduced via AI" },
  { number: "25–30%", label: "Compliance Errors Reduced via Digital Systems" },
];
export const professionalServiceBlueContent = {
  title: "UNDERSTANDING PROFESSIONAL FIRMS BETTER ",
  paragraphs: [
    "In professional services, one weak link impacts the entire firm. What leaders need is clarity, not complexity. Pestel & Partners helps firms rebuild structure, sharpen performance, and create service models that grow stronger over time.",
  ],
  image: "/assets/images/finance-team.png",
  highlights: [
    {
      icon: "/assets/icons/path-to-icon1.png",
      text: "Firms lose 8-12% in annual revenue due to poor capacity planning.",
    },
    {
      icon: "/assets/icons/path-to-icon2.png",
      text: "Nearly 40% of project delays stem from fragmented workflows.",
    },
    {
      icon: "/assets/icons/path-to-icon3.png",
      text: "Pricing gaps and rising delivery costs reduce profit margins by 6-9%.",
    },
  ],
};
export const publicSectorBlueContentHeading = {
  title: "TRUSTED OUTCOMES ",
  subtitle:
    "Enabling public institutions to operate with greater transparency and efficiency. ",
};
export const publicSectorBlueContentStats = [
  { number: "35%", label: "R&D Cost Increase Over Last Decade" },
  { number: "6–8%", label: "Expected Annual Global R&D Growth" },
  { number: "40%", label: "Early-stage Development Time Reduced via AI" },
  { number: "25–30%", label: "Compliance Errors Reduced via Digital Systems" },
];
export const publicSectorBlueContent = {
  title: "MAKING PUBLIC SYSTEMS STRONGER ",
  paragraphs: [
    "Public sector challenges often overlap. Budget strain affects service quality, fragmented data slows decisions, and digital gaps impact transparency. Pestel & Partners helps governments strengthen operations and build more responsive, resilient public services.",
  ],
  image: "/assets/images/finance-team.png",
  highlights: [
    {
      icon: "/assets/icons/path-to-icon1.png",
      text: "Up to 35–45% of public service backlogs.",
    },
    {
      icon: "/assets/icons/path-to-icon2.png",
      text: "Governments lose 8–12% of annual spending.",
    },
    {
      icon: "/assets/icons/path-to-icon3.png",
      text: "Nearly 60% of public institutions report limited real-time data visibility.",
    },
  ],
};
export const realEstateConstructionBlueHeading = {
  title: "RESULTS THAT MATTER ",
  subtitle:
    "Turning project complexity into measurable construction and portfolio gains. ",
};
export const realEstateConstructionBlueStats = [
  { number: "35%", label: "R&D Cost Increase Over Last Decade" },
  { number: "6–8%", label: "Expected Annual Global R&D Growth" },
  { number: "40%", label: "Early-stage Development Time Reduced via AI" },
  { number: "25–30%", label: "Compliance Errors Reduced via Digital Systems" },
];
export const realEstateConstructionBlueContent = {
  title: "CLARITY IN CONSTRUCTION PROJECT DECISIONS ",
  paragraphs: [
    "Challenges in real estate and construction rarely stand alone—rising costs influence cash flow, delays impact investor confidence, and operational blind spots slow every project. Pestel & Partners helps organizations build more predictable and resilient project outcomes.",
    
  ],
  image: "/assets/images/finance-team.png",
  highlights: [
    {
      icon: "/assets/icons/path-to-icon1.png",
      text: "Nearly 70% of construction projects exceed budget.",
    },
    {
      icon: "/assets/icons/path-to-icon2.png",
      text: "Industry studies show 60–80% of projects experience schedule slippage.",
    },
    {
      icon: "/assets/icons/path-to-icon3.png",
      text: "Up to 30% of project costs can be tied to rework.",
    },
  ],
};
export const mediaEntertainmentHeading = {
  title: "RESULTS FOR MEDIA LEADERS",
  subtitle:
    "Delivering stronger content pipelines, engaged audiences, and stable revenue models.",
};

export const mediaEntertainmentStats = [
  { number: "5–7", label: "Average Platforms Used by Global Audiences" },
  { number: "20–30%", label: "Rise in Scripted Content Budgets" },
  { number: "55%", label: "Media Revenue from Digital & Streaming" },
  { number: "40%", label: "Increase in Multi-Platform Production Costs" },
];

export const mediaEntertainmentBlueContent = {
  title: "MAKING THE ENTERTAINMENT LANDSCAPE CLEARER",
  paragraphs: [
    "Media keeps changing faster than businesses can adjust; audiences jump platforms often, production costs rise every year, and digital revenue models never stay the same for long.Pestel & Partners helps media companies cut through industry noise and make decisions grounded, not guesswork.",
  ],
  image: "/assets/images/finance-team.png",
  highlights: [
    {
      icon: "/assets/icons/path-to-icon1.png",
      text: "Global audiences now watch content across 5–7 platforms on average.",
    },
    {
      icon: "/assets/icons/path-to-icon2.png",
      text: "Scripted production budgets have increased by 20–30%.",
    },
    {
      icon: "/assets/icons/path-to-icon3.png",
      text: "Digital & streaming now contribute over 55% of total media revenues.",
    },
  ],
};
export const telecommunicationHeading = {
  title: "RESULT-DRIVEN PERFORMANCE",
  subtitle: "Helping operators improve network ROI and build stronger financial models.",
};

export const telecommunicationStats = [
  { number: "25–30%", label: "Annual Growth in Mobile Data Consumption" },
  { number: "18–25%", label: "Average Telecom User Churn" },
  { number: "20–25%", label: "Rise in 5G Infrastructure Costs" },
  { number: "40%", label: "Network Load Increase in High-Traffic Cities" },
];

export const telecommunicationBlueContent = {
  title: "MAKING TELECOM COMPLEXITY CLEAR",
  paragraphs: [
    "Telecom leaders today battle with network loads, service drops, and shifting investment decisions overnight.Pestel & Partners helps operators cut through this complexity with clearer insights for the next era of connectivity.",
  ],
  image: "/assets/images/finance-team.png",
  highlights: [
    {
      icon: "/assets/icons/path-to-icon1.png",
      text: "Mobile data consumption has grown 25–30% annually.",
    },
    {
      icon: "/assets/icons/path-to-icon2.png",
      text: "Telecom churn rates average between 18–25%.",
    },
    {
      icon: "/assets/icons/path-to-icon3.png",
      text: "5G infrastructure costs have risen by 20–25%.",
    },
  ],
};
export const technologyHeading = {
  title: "DIGITAL SUCCESS DELIVERED",
  subtitle: "Helping tech companies unlock strong customer grip and sustainable growth.",
};

export const technologyStats = [
  { number: "60%", label: "Tech Teams Reporting Slow Releases" },
  { number: "25–35%", label: "Cloud Budgets Lost Annually" },
  { number: "15–25%", label: "Average SaaS Product Churn" },
  { number: "10–15%", label: "Dev Cycle Slowdown Due to Fragmentation" },
];

export const technologyBlueContent = {
  title: "CLARITY IN TECHNOLOGY DECISIONS",
  paragraphs: [
    "Technology companies operate in a landscape of new competitors, shifting tools, and fast-changing customer expectations every quarter.",
    "Pestel & Partners helps tech leaders find clarity on what truly drives growth.",
  ],
  image: "/assets/images/finance-team.png",
  highlights: [
    {
      icon: "/assets/icons/path-to-icon1.png",
      text: "Over 60% of tech teams mention slow product releases.",
    },
    {
      icon: "/assets/icons/path-to-icon2.png",
      text: "Companies lose 25–35% of cloud budgets.",
    },
    {
      icon: "/assets/icons/path-to-icon3.png",
      text: "SaaS products see 15–25% annual churn.",
    },
  ],
};
